import { Terminal } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import "@xterm/xterm/css/xterm.css";
import { api } from "./api.ts";

export type TerminalStatus = "preparing" | "connecting" | "connected" | "disconnected" | "error";

const THEME = {
  background: "#0b1020",
  foreground: "#dbe4f0",
  cursor: "#4ade80",
  cursorAccent: "#0b1020",
  selectionBackground: "#334155",
  black: "#1e293b",
  red: "#f87171",
  green: "#4ade80",
  yellow: "#facc15",
  blue: "#60a5fa",
  magenta: "#c084fc",
  cyan: "#22d3ee",
  white: "#e2e8f0",
  brightBlack: "#64748b",
  brightRed: "#fca5a5",
  brightGreen: "#86efac",
  brightYellow: "#fde047",
  brightBlue: "#93c5fd",
  brightMagenta: "#d8b4fe",
  brightCyan: "#67e8f9",
  brightWhite: "#f8fafc",
};

/** Terminal web relié au shell du conteneur de l'apprenant. */
export class TerminalSession {
  private readonly term: Terminal;
  private readonly fit = new FitAddon();
  private readonly resizeObserver: ResizeObserver;
  private socket: WebSocket | null = null;
  private disposed = false;
  private readonly moduleId: string;
  private readonly onStatus: (status: TerminalStatus, detail?: string) => void;

  constructor(
    host: HTMLElement,
    moduleId: string,
    onStatus: (status: TerminalStatus, detail?: string) => void,
  ) {
    this.moduleId = moduleId;
    this.onStatus = onStatus;
    this.term = new Terminal({
      fontFamily: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
      fontSize: 14,
      lineHeight: 1.2,
      cursorBlink: true,
      scrollback: 5000,
      theme: THEME,
      allowProposedApi: false,
    });
    this.term.loadAddon(this.fit);
    this.term.open(host);
    this.term.onData((data) => this.send({ t: "i", d: data }));
    this.term.onResize(({ cols, rows }) => this.send({ t: "r", c: cols, r: rows }));
    this.resizeObserver = new ResizeObserver(() => this.refit());
    this.resizeObserver.observe(host);
    // La police est chargée de façon asynchrone : on recalcule les dimensions une fois prête.
    void document.fonts.ready.then(() => this.refit());
  }

  private refit(): void {
    if (this.disposed) return;
    try {
      this.fit.fit();
    } catch {
      // Conteneur masqué ou de taille nulle : rien à ajuster.
    }
  }

  private send(message: object): void {
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(JSON.stringify(message));
  }

  async connect(): Promise<void> {
    this.socket?.close();
    this.socket = null;
    this.onStatus("preparing");
    try {
      await api.prepareEnvironment(this.moduleId);
    } catch (err) {
      this.onStatus("error", err instanceof Error ? err.message : String(err));
      return;
    }
    if (this.disposed) return;

    this.onStatus("connecting");
    const protocol = location.protocol === "https:" ? "wss:" : "ws:";
    const socket = new WebSocket(`${protocol}//${location.host}/api/modules/${this.moduleId}/terminal`);
    socket.binaryType = "arraybuffer";
    this.socket = socket;

    socket.onopen = () => {
      this.term.reset();
      this.refit();
      this.send({ t: "r", c: this.term.cols, r: this.term.rows });
      this.onStatus("connected");
      this.term.focus();
    };
    socket.onmessage = (event) => {
      this.term.write(typeof event.data === "string" ? event.data : new Uint8Array(event.data as ArrayBuffer));
    };
    socket.onclose = (event) => {
      if (this.socket !== socket || this.disposed) return;
      this.socket = null;
      const reason = event.code === 1000 ? "La session du terminal est terminée." : event.reason || "Connexion perdue.";
      this.term.write(`\r\n\x1b[2m[${reason}]\x1b[0m\r\n`);
      this.onStatus("disconnected", reason);
    };
  }

  focus(): void {
    this.term.focus();
  }

  dispose(): void {
    this.disposed = true;
    this.resizeObserver.disconnect();
    this.socket?.close();
    this.term.dispose();
  }
}
